import React, { useState } from 'react';
import { Shield, Sparkles, Gem, AlertTriangle, Star, Check, X, ShipWheel } from 'lucide-react';
import { GemstoneIcon, RudrakshaIcon, ElephantIcon, SunYogaIcon, MoonYogaIcon, ScalesIcon, YogaSparkleIcon } from './AstroIcons';
import { isCurrentPeriod, isFutureDate } from './utils';
import SectionHeader from './SectionHeader';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';
import { getPlanetName, normalizeNodeTerms } from '../../../../utils/planetNames';

interface ReportTabProps {
    kundliData: any;
    language: 'english' | 'hindi';
}

const ReportTab: React.FC<ReportTabProps> = ({ kundliData, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    const [activeSubTab, setActiveSubTab] = useState<'yog' | 'dosh' | 'gemstone' | 'rudraksha'>('yog');

    const subTabs = [
        { id: 'yog' as const, label: t.yogas || 'Yogas', shortLabel: t.yogas || 'Yogas', icon: Sparkles },
        { id: 'dosh' as const, label: t.doshasLabel || 'Doshas', shortLabel: t.doshasLabel || 'Doshas', icon: Shield },
        { id: 'gemstone' as const, label: t.gemstones || 'Gemstones', shortLabel: t.gemstonesShort || 'Gems', icon: Gem },
        { id: 'rudraksha' as const, label: t.rudraksha || 'Rudraksha', shortLabel: t.rudrakshaShort || 'Rudra', icon: ShipWheel },
    ];

    const analysis = kundliData.detailed_analysis || {};
    const gemstones = kundliData.gemstones || {};
    const rudraksha = kundliData.rudraksha || {};

    // Render Yogas Sub-tab
    const renderYogasTab = () => {
        const yogasList = [
            { key: 'gaja_kesari_yoga', name: t.gajaKesariYoga || 'Gaja Kesari Yoga', Icon: ElephantIcon, iconColor: "text-blue-400" },
            { key: 'budha_aditya_yoga', name: t.budhaAdityaYoga || 'Budha-Aditya Yoga', Icon: SunYogaIcon, iconColor: "text-amber-400" },
            { key: 'chandra_mangala_yoga', name: t.chandraMangalaYoga || 'Chandra-Mangala Yoga', Icon: MoonYogaIcon, iconColor: "text-blue-200" },
            { key: 'dharma_karmadhipati_yoga', name: t.dharmaKarmaYoga || 'Dharma-Karmadhipati Yoga', Icon: ScalesIcon, iconColor: "text-emerald-400" },
        ];

        const mahapurushaYogas = analysis.mahapurusha_yogas || {};

        return (
            <div className="space-y-6 md:space-y-8">
                {/* Summary Card */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-900/30 shrink-0">
                            <YogaSparkleIcon className="w-6 h-6 md:w-8 md:h-8" color="text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-2xl font-bold text-white">{t.totalYogas || 'Total Yogas Found'}</h2>
                            <p className="text-2xl md:text-3xl font-bold text-emerald-400 mt-1">{analysis.total_yogas || 0}</p>
                        </div>
                    </div>
                </div>

                {/* Main Yogas */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <SectionHeader
                        icon={Sparkles}
                        title={t.mainYogas || 'Main Yogas'}
                        description={t.mainYogasDesc || 'Key planetary combinations in your chart'}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        {yogasList.map((yoga) => {
                            const yogaData = analysis[yoga.key] || {};
                            const isPresent = yogaData.present;
                            const YogaIcon = yoga.Icon;

                            return (
                                <div
                                    key={yoga.key}
                                    className={`p-4 md:p-6 rounded-xl md:rounded-2xl border transition-all duration-300 ${isPresent
                                        ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                                        : 'bg-red-500/5 border-red-500/20 hover:border-red-500/40'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center ${isPresent
                                            ? 'bg-emerald-500/20 text-emerald-500'
                                            : 'bg-red-500/20 text-red-500'
                                            }`}>
                                            <YogaIcon className="w-5 h-5 md:w-6 md:h-6" color={isPresent ? "text-emerald-400" : "text-red-400"} />
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase ${isPresent
                                            ? 'bg-emerald-500 text-white'
                                            : 'bg-red-500/80 text-white'
                                            }`}>
                                            {isPresent ? (t.present || 'Present') : (t.absent || 'Absent')}
                                        </span>
                                    </div>
                                    <h3 className="text-sm md:text-base font-bold text-white mb-1">{yoga.name}</h3>
                                    {isPresent && yogaData.strength && (
                                        <p className="text-white/50 text-xs mb-3">{t.strength || 'Strength'}: {yogaData.strength}</p>
                                    )}
                                    {isPresent && yogaData.benefits && yogaData.benefits.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-white/10">
                                            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{t.benefits || 'Benefits'}</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {yogaData.benefits.map((benefit: string, idx: number) => (
                                                    <span key={idx} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 text-xs rounded-lg">
                                                        {benefit}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pancha Mahapurusha Yogas */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <SectionHeader
                        icon={Star}
                        title={t.panchaMahapurusha || 'Pancha Mahapurusha Yogas'}
                        description={t.panchaMahapurushaDesc || 'Five great personality yogas'}
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mt-6">
                        {Object.entries(mahapurushaYogas).map(([key, data]: [string, any]) => (
                            <div
                                key={key}
                                className={`p-3 md:p-4 rounded-xl md:rounded-2xl border text-center transition-all duration-300 ${data.present
                                    ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                                    : 'bg-white/[0.04] border-white/10 hover:border-white/20'
                                    }`}
                            >
                                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${data.present ? 'bg-emerald-500/20 text-emerald-500' : 'bg-white/10 text-white/40'
                                    }`}>
                                    {data.present ? <Check size={18} className="md:w-5 md:h-5" /> : <X size={18} className="md:w-5 md:h-5" />}
                                </div>
                                <p className={`text-xs md:text-sm font-bold font-sans ${data.present ? 'text-emerald-400' : 'text-white/50'}`}>
                                    {language === 'hindi'
                                        ? (data.name || key.replace('_mahapurusha', '').replace('_', ' ')).replace('Yoga', 'योग')
                                        : normalizeNodeTerms(data.name || key.replace('_mahapurusha', '').replace('_', ' '), language)}
                                </p>
                                {data.present && data.planet && (
                                    <p className="text-[10px] md:text-xs font-sans text-white/50 mt-1">{getPlanetName(data.planet, t.planetNames)} in {data.sign}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Render Doshas Sub-tab
    const renderDoshasTab = () => {
        return (
            <div className="space-y-6 md:space-y-8">
                {/* Summary Card */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-red-600 to-rose-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-red-900/30 shrink-0">
                            <Shield size={24} className="md:w-8 md:h-8" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-2xl font-bold text-white">{t.totalDoshas || 'Total Doshas Found'}</h2>
                            <p className="text-2xl md:text-3xl font-bold text-red-400 mt-1">{analysis.total_doshas || 0}</p>
                        </div>
                    </div>
                </div>

                {/* Doshas Grid */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <SectionHeader
                        icon={Shield}
                        title={t.doshaAnalysis || 'Dosha Analysis'}
                        description={t.doshaAnalysisDesc || 'Planetary afflictions in your chart'}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6">
                        {/* Manglik Dosha */}
                        <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border transition-all duration-300 ${analysis.manglik_dosha?.manglik
                            ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50'
                            : 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                            }`}>
                            <div className="flex justify-between items-start mb-4">
                                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center ${analysis.manglik_dosha?.manglik
                                    ? 'bg-red-500/20 text-red-500'
                                    : 'bg-emerald-500/20 text-emerald-500'
                                    }`}>
                                    <Shield size={20} className="md:w-6 md:h-6" />
                                </div>
                                <span className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase ${analysis.manglik_dosha?.manglik
                                    ? 'bg-red-500 text-white'
                                    : 'bg-emerald-500 text-white'
                                    }`}>
                                    {analysis.manglik_dosha?.manglik ? t.present : t.absent}
                                </span>
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-white mb-1">{t.manglikDosha}</h3>
                            <p className="text-white/50 text-xs mb-3">{t.manglikDesc}</p>
                            {analysis.manglik_dosha?.description && (
                                <p className="text-white/60 text-sm mb-3 font-sans">{normalizeNodeTerms(analysis.manglik_dosha.description, language)}</p>
                            )}
                            {analysis.manglik_dosha?.severity && (
                                <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                                    <span className="text-xs text-white/40 uppercase">{t.severity}:</span>
                                    <span className={`text-sm font-bold ${analysis.manglik_dosha.severity === 'High' ? 'text-red-500' :
                                        analysis.manglik_dosha.severity === 'Medium' ? 'text-orange-500' : 'text-emerald-500'
                                        }`}>
                                        {analysis.manglik_dosha.severity}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Kalsarpa Dosha */}
                        <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border transition-all duration-300 ${analysis.kalsarpa_dosha?.present
                            ? 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50'
                            : 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                            }`}>
                            <div className="flex justify-between items-start mb-4">
                                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center ${analysis.kalsarpa_dosha?.present
                                    ? 'bg-amber-500/20 text-amber-500'
                                    : 'bg-emerald-500/20 text-emerald-500'
                                    }`}>
                                    <Sparkles size={20} className="md:w-6 md:h-6" />
                                </div>
                                <span className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase ${analysis.kalsarpa_dosha?.present
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-emerald-500 text-white'
                                    }`}>
                                    {analysis.kalsarpa_dosha?.present ? t.present : t.absent}
                                </span>
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-white mb-1">{t.kalsarpaAnalysis}</h3>
                            <p className="text-white/50 text-xs mb-3">{t.kalsarpaDesc}</p>
                            {analysis.kalsarpa_dosha?.description && (
                                <p className="text-white/60 text-sm font-sans mb-3">{normalizeNodeTerms(analysis.kalsarpa_dosha.description, language)}</p>
                            )}
                            {analysis.kalsarpa_dosha?.present && analysis.kalsarpa_dosha?.type && (
                                <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                                    <span className="text-xs text-white/40 uppercase">{t.type}:</span>
                                    <span className="text-sm font-bold font-sans text-amber-500">{normalizeNodeTerms(analysis.kalsarpa_dosha.type, language)}</span>
                                </div>
                            )}
                        </div>

                        {/* Grahan Dosha */}
                        <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border transition-all duration-300 ${analysis.grahan_dosha?.present
                            ? 'bg-purple-500/10 border-purple-500/30 hover:border-purple-500/50'
                            : 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                            }`}>
                            <div className="flex justify-between items-start mb-4">
                                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center ${analysis.grahan_dosha?.present
                                    ? 'bg-purple-500/20 text-purple-500'
                                    : 'bg-emerald-500/20 text-emerald-500'
                                    }`}>
                                    <AlertTriangle size={20} className="md:w-6 md:h-6" />
                                </div>
                                <span className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase ${analysis.grahan_dosha?.present
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-emerald-500 text-white'
                                    }`}>
                                    {analysis.grahan_dosha?.present ? t.present : t.absent}
                                </span>
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-white mb-1">{t.grahanDosha || 'Grahan Dosha'}</h3>
                            <p className="text-white/50 text-xs mb-3">{t.grahanDesc || 'Eclipse combination affecting mental peace and clarity.'}</p>
                            {analysis.grahan_dosha?.description && (
                                <p className="text-white/60 text-sm font-sans">{normalizeNodeTerms(analysis.grahan_dosha.description, language)}</p>
                            )}
                        </div>

                        {/* Pitra Dosha */}
                        <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border transition-all duration-300 ${analysis.pitra_dosha?.present
                            ? 'bg-orange-500/10 border-orange-500/30 hover:border-orange-500/50'
                            : 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                            }`}>
                            <div className="flex justify-between items-start mb-4">
                                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center ${analysis.pitra_dosha?.present
                                    ? 'bg-orange-500/20 text-orange-500'
                                    : 'bg-emerald-500/20 text-emerald-500'
                                    }`}>
                                    <Star size={20} className="md:w-6 md:h-6" />
                                </div>
                                <span className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase ${analysis.pitra_dosha?.present
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-emerald-500 text-white'
                                    }`}>
                                    {analysis.pitra_dosha?.present ? t.present : t.absent}
                                </span>
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-white mb-1">{t.pitraDosha || 'Pitra Dosha'}</h3>
                            <p className="text-white/50 text-xs mb-3">{t.pitraDesc || 'Ancestral karma affecting family lineage and blessings.'}</p>
                            {analysis.pitra_dosha?.description && (
                                <p className="text-white/60 text-sm font-sans">{normalizeNodeTerms(analysis.pitra_dosha.description, language)}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sadesati Section */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <SectionHeader
                        icon={Shield}
                        title={t.sadesatiAnalysis}
                        description={t.sadesatiDesc}
                    />

                    <div className={`p-4 md:p-5 rounded-xl md:rounded-2xl border mt-6 ${analysis.sadesati_dosha?.present
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-emerald-500/10 border-emerald-500/30'
                        }`}>
                        <div className="flex items-center gap-3 md:gap-4">
                            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center ${analysis.sadesati_dosha?.present
                                ? 'bg-amber-500 text-white'
                                : 'bg-emerald-500 text-white'
                                }`}>
                                {analysis.sadesati_dosha?.present ? <AlertTriangle size={22} className="md:w-6 md:h-6" /> : <Check size={22} className="md:w-6 md:h-6" />}
                            </div>
                            <div>
                                <h4 className={`text-base md:text-lg font-bold ${analysis.sadesati_dosha?.present ? 'text-amber-400' : 'text-emerald-400'
                                    }`}>
                                    {analysis.sadesati_dosha?.present ? t.inSadesati : t.notInSadesati}
                                </h4>
                                {analysis.sadesati_dosha?.current_phase && (
                                    <p className="text-xs md:text-sm text-white/60">{t.currentPhase}: {analysis.sadesati_dosha.current_phase}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-6">
                        {/* Currently Active */}
                        <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4">
                            <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/40 mb-1 md:mb-2">{t.currentlyActive}</p>
                            <p className={`text-base md:text-lg font-bold font-sans ${analysis.sadesati_dosha?.present ? 'text-amber-400' : 'text-emerald-400'
                                }`}>
                                {analysis.sadesati_dosha?.current_sadesati_status || (analysis.sadesati_dosha?.present ? t.yes : t.no)}
                            </p>
                        </div>

                        {/* Current Phase */}
                        <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4">
                            <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/40 mb-1 md:mb-2">{t.currentPhase}</p>
                            <p className="text-base md:text-lg font-bold font-sans text-white">
                                {analysis.sadesati_dosha?.current_phase || t.none}
                            </p>
                        </div>

                        {/* Next Starts On */}
                        <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4">
                            <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/40 mb-1 md:mb-2">{t.nextStartsOn}</p>
                            <p className="text-base md:text-lg font-bold font-sans text-white">
                                {analysis.sadesati_dosha?.next_start || (language === 'hindi' ? 'अज्ञात' : 'Unknown')}
                            </p>
                        </div>

                        {/* Severity Level */}
                        <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4">
                            <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/40 mb-1 md:mb-2">{t.severityLevel}</p>
                            <p className="text-base md:text-lg font-bold font-sans text-white">
                                {analysis.sadesati_dosha?.severity || (language === 'hindi' ? 'कोई नहीं' : 'None')}
                            </p>
                        </div>
                    </div>

                    {/* Sadesati Periods */}
                    {
                        analysis.sadesati_dosha?.periods && analysis.sadesati_dosha.periods.length > 0 && (
                            <div className="mt-6">
                                <p className="text-xs text-white/40 uppercase tracking-widest mb-3">{t.timelineSadesati}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {analysis.sadesati_dosha.periods.map((period: any, idx: number) => {
                                        const isCurrent = isCurrentPeriod(period.start_date, period.end_date);
                                        const isFuture = isFutureDate(period.start_date);
                                        const isPast = !isCurrent && !isFuture;

                                        return (
                                            <div
                                                key={idx}
                                                className={`p-3 md:p-4 rounded-xl border transition-all duration-300 ${isCurrent
                                                    ? 'bg-amber-500/10 border-amber-500/30'
                                                    : isFuture
                                                        ? 'bg-blue-500/10 border-blue-500/30'
                                                        : 'bg-white/[0.04] border-white/10'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className={`text-xs font-bold ${period.type === 'Peak' ? 'text-red-400' :
                                                        period.type === 'Rising' ? 'text-amber-400' : 'text-blue-400'
                                                        }`}>
                                                        {period.type}
                                                    </span>
                                                    {isCurrent && (
                                                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[9px] md:text-[10px] font-bold rounded-full">
                                                            {t.active}
                                                        </span>
                                                    )}
                                                    {isFuture && (
                                                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[9px] md:text-[10px] font-bold rounded-full">
                                                            {t.upcoming}
                                                        </span>
                                                    )}
                                                    {isPast && (
                                                        <span className="px-2 py-0.5 bg-white/10 text-white/40 text-[9px] md:text-[10px] font-bold rounded-full">
                                                            {t.completed}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[10px] md:text-xs font-sans text-white/40">{period.sign}</p>
                                                <p className="text-xs md:text-sm font-sans text-white/70 mt-1">
                                                    {period.start_date} - {period.end_date}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    // Render Gemstones Sub-tab
    const renderGemstonesTab = () => {
        return (
            <div className="space-y-6 md:space-y-8">
                {/* Header */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-purple-900/30 shrink-0">
                            <Gem size={24} className="md:w-8 md:h-8" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-2xl font-bold text-white">{t.gemstoneRecommendations || 'Gemstone Recommendations'}</h2>
                            <p className="text-primary-200/50 text-[11px] md:text-sm font-light italic mt-1 leading-tight">{t.gemstoneDesc || 'Based on your ascendant and planetary positions'}</p>
                        </div>
                    </div>
                </div>

                {/* Gemstones Grid */}
                <div className="space-y-4 md:space-y-6">
                    {/* Life Stone */}
                    <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8 relative overflow-hidden group hover:border-amber-500/30 transition-colors">
                        <div className="absolute top-4 right-4 md:top-6 md:right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                            <GemstoneIcon className="w-24 h-24 md:w-32 md:h-32" color="text-amber-500" />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 md:gap-4 mb-6">
                                <div className="p-3 md:p-4 bg-amber-500/20 rounded-xl md:rounded-2xl">
                                    <GemstoneIcon className="w-6 h-6 md:w-8 md:h-8" color="text-amber-400" />
                                </div>
                                <div>
                                    <h3 className="text-base md:text-lg font-bold text-white mb-0.5">{gemstones?.life?.stone || '---'}</h3>
                                    <p className="text-[10px] md:text-xs text-amber-400 uppercase tracking-widest font-bold">{t.lifeStone}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                <div className="bg-black/20 rounded-xl p-3 md:p-4">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.rulingPlanet}</span>
                                    <span className="text-white font-bold text-base md:text-lg">{getPlanetName(gemstones?.life?.planet || '---', t.planetNames)}</span>
                                </div>
                                <div className="bg-black/20 rounded-xl p-3 md:p-4 md:col-span-2">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.benefits}</span>
                                    <span className="text-white/90 text-xs md:text-sm leading-relaxed font-sans">{gemstones?.life?.description || '---'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lucky Stone */}
                    {gemstones?.lucky && (
                        <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8 relative overflow-hidden group hover:border-purple-500/30 transition-colors">
                            <div className="absolute top-4 right-4 md:top-6 md:right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                                <GemstoneIcon className="w-24 h-24 md:w-32 md:h-32" color="text-purple-500" />
                            </div>

                            <div className="relative z-10">
                                <div className="flex items-center gap-3 md:gap-4 mb-6">
                                    <div className="p-3 md:p-4 bg-purple-500/20 rounded-xl md:rounded-2xl">
                                        <GemstoneIcon className="w-6 h-6 md:w-8 md:h-8" color="text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-base md:text-lg font-bold text-white mb-0.5">{gemstones.lucky?.stone || '---'}</h3>
                                        <p className="text-[10px] md:text-xs text-purple-400 uppercase tracking-widest font-bold">{t.luckyStone || 'Lucky Stone'}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                    <div className="bg-black/20 rounded-xl p-3 md:p-4">
                                        <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.rulingPlanet}</span>
                                        <span className="text-white font-bold text-base md:text-lg">{getPlanetName(gemstones.lucky?.planet || '---', t.planetNames)}</span>
                                    </div>
                                    <div className="bg-black/20 rounded-xl p-3 md:p-4 md:col-span-2">
                                        <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.benefits}</span>
                                        <span className="text-white/90 text-xs md:text-sm leading-relaxed font-sans">{gemstones.lucky?.description || '---'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Fortune Stone */}
                    <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8 relative overflow-hidden group hover:border-emerald-500/30 transition-colors">
                        <div className="absolute top-4 right-4 md:top-6 md:right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                            <GemstoneIcon className="w-24 h-24 md:w-32 md:h-32" color="text-emerald-500" />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 md:gap-4 mb-6">
                                <div className="p-3 md:p-4 bg-emerald-500/20 rounded-xl md:rounded-2xl">
                                    <GemstoneIcon className="w-6 h-6 md:w-8 md:h-8" color="text-emerald-400" />
                                </div>
                                <div>
                                    <h3 className="text-base md:text-lg font-bold text-white mb-0.5">{gemstones?.fortune?.stone || '---'}</h3>
                                    <p className="text-[10px] md:text-xs text-emerald-400 uppercase tracking-widest font-bold">{t.fortuneStone}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                <div className="bg-black/20 rounded-xl p-3 md:p-4">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.rulingPlanet}</span>
                                    <span className="text-white font-bold text-base md:text-lg">{getPlanetName(gemstones?.fortune?.planet || '---', t.planetNames)}</span>
                                </div>
                                <div className="bg-black/20 rounded-xl p-3 md:p-4 md:col-span-2">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.benefits}</span>
                                    <span className="text-white/90 text-xs md:text-sm leading-relaxed font-sans">{gemstones?.fortune?.description || '---'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Note */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl md:rounded-2xl p-4 md:p-5">
                    <p className="text-xs md:text-sm text-amber-200/80">
                        <strong>{t.note || 'Note'}:</strong> {t.gemstoneNote || 'Please consult with a qualified astrologer before wearing any gemstone. The effects vary based on individual charts and current planetary periods.'}
                    </p>
                </div>
            </div>
        );
    };

    // Render Rudraksha Sub-tab
    const renderRudrakshTab = () => {
        return (
            <div className="space-y-6 md:space-y-8">
                {/* Header */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-600 to-amber-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-900/30 shrink-0">
                            <ShipWheel size={24} className="md:w-8 md:h-8" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-2xl font-bold text-white">{t.rudrakshRecommendations || 'Rudraksha Recommendations'}</h2>
                            <p className="text-primary-200/50 text-[11px] md:text-sm font-light italic mt-1 leading-tight">{t.rudrakshDesc || 'Sacred beads for spiritual and planetary balance'}</p>
                        </div>
                    </div>
                </div>

                {/* Rudraksha Grid */}
                <div className="space-y-4 md:space-y-6">
                    {/* Life Rudraksha */}
                    <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8 relative overflow-hidden group hover:border-amber-700/30 transition-colors">
                        <div className="absolute top-4 right-4 md:top-6 md:right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                            <RudrakshaIcon className="w-24 h-24 md:w-32 md:h-32" color="text-amber-700" />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 md:gap-4 mb-6">
                                <div className="p-3 md:p-4 bg-amber-700/20 rounded-xl md:rounded-2xl">
                                    <RudrakshaIcon className="w-6 h-6 md:w-8 md:h-8" color="text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="text-base md:text-lg font-bold text-white mb-0.5">{rudraksha?.life?.bead || '---'}</h3>
                                    <p className="text-[10px] md:text-xs text-amber-500 uppercase tracking-widest font-bold">{t.lifeRudraksha}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                <div className="bg-black/20 rounded-xl p-3 md:p-4">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.rulingPlanet}</span>
                                    <span className="text-white font-bold text-base md:text-lg">{getPlanetName(rudraksha?.life?.planet || '---', t.planetNames)}</span>
                                </div>
                                <div className="bg-black/20 rounded-xl p-3 md:p-4 md:col-span-2">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.benefits}</span>
                                    <span className="text-white/90 text-xs md:text-sm leading-relaxed font-sans">{rudraksha?.life?.description || '---'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Fortune Rudraksha */}
                    <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8 relative overflow-hidden group hover:border-orange-500/30 transition-colors">
                        <div className="absolute top-4 right-4 md:top-6 md:right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                            <RudrakshaIcon className="w-24 h-24 md:w-32 md:h-32" color="text-orange-600" />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 md:gap-4 mb-6">
                                <div className="p-3 md:p-4 bg-orange-500/20 rounded-xl md:rounded-2xl">
                                    <RudrakshaIcon className="w-6 h-6 md:w-8 md:h-8" color="text-orange-500" />
                                </div>
                                <div>
                                    <h3 className="text-base md:text-lg font-bold text-white mb-0.5">{rudraksha?.fortune?.bead || '---'}</h3>
                                    <p className="text-[10px] md:text-xs text-orange-400 uppercase tracking-widest font-bold">{t.fortuneRudraksha}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                <div className="bg-black/20 rounded-xl p-3 md:p-4">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.rulingPlanet}</span>
                                    <span className="text-white font-bold text-base md:text-lg">{getPlanetName(rudraksha?.fortune?.planet || '---', t.planetNames)}</span>
                                </div>
                                <div className="bg-black/20 rounded-xl p-3 md:p-4 md:col-span-2">
                                    <span className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider block mb-1 md:mb-2">{t.benefits}</span>
                                    <span className="text-white/90 text-xs md:text-sm leading-relaxed font-sans">{rudraksha?.fortune?.description || '---'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Note */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl md:rounded-2xl p-4 md:p-5">
                    <p className="text-xs md:text-sm text-amber-200/80">
                        <strong>{t.note || 'Note'}:</strong> {t.rudrakshNote || 'Rudraksha beads should be properly energized and worn after consulting with a qualified astrologer. Regular meditation and chanting enhance their spiritual benefits.'}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500 font-sans">
            {/* Sub-tabs Navigation */}
            <div className="bg-white/[0.08] border border-white/20 rounded-[20px] md:rounded-[24px] p-1.5 md:p-2">
                <div className="flex gap-1 md:gap-2">
                    {subTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-4 py-2.5 md:py-3 rounded-xl md:rounded-2xl font-bold text-[11px] md:text-sm transition-all duration-300 ${activeSubTab === tab.id
                                ? 'bg-gradient-to-r from-primary-600 to-indigo-600 text-white shadow-lg shadow-primary-900/30'
                                : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                                }`}
                        >
                            <tab.icon size={14} className="md:w-4 md:h-4 flex-shrink-0" />
                            <span className="truncate hidden sm:inline">{tab.label}</span>
                            <span className="truncate inline sm:hidden">{(tab as any).shortLabel || tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Sub-tab Content */}
            <div>
                {activeSubTab === 'yog' && renderYogasTab()}
                {activeSubTab === 'dosh' && renderDoshasTab()}
                {activeSubTab === 'gemstone' && renderGemstonesTab()}
                {activeSubTab === 'rudraksha' && renderRudrakshTab()}
            </div>
        </div>
    );
};

export default ReportTab;
